// Funsquare Couch Customiser — core logic (state, pricing, line items, URL sync).
// Dot-cushion model: two independent cushions, each with its own fabric colour,
// wantsText flag, text, font, and text colour. Text upsell is Microsuede-only.

// wantsText defaults to false (pre-selected "No Thank You") so ATC isn't blocked
// when a customer enables dotCushions but doesn't want custom text — consistent
// with the other optional steps. Customer can still opt in via the dropdown.
const EMPTY_CUSHION = { fabric: null, wantsText: false, text: '', font: null, textColour: null }

export function createStore(config) {
  // Optional add-ons default to "No thank you" (type 'none', enabled false) so
  // the ATC button can be clicked as soon as the required fabric/colour choices
  // are made. Customer can still opt in to armrests / extra fun / dot cushions.
  let state = {
    fabricType: null,
    baseThickness: null,
    upperBase: null,
    lowerBase: null,
    trapezium: null,
    armrest: { type: 'none', colour: null },
    extraFun: { type: 'none', colour: null },
    dotCushion: {
      enabled: false,
      cushion1: { ...EMPTY_CUSHION },
      cushion2: { ...EMPTY_CUSHION },
    },
  }
  const listeners = new Set()
  function notify() { for (const l of listeners) l(state) }

  function set(path, value) {
    const parts = path.split('.')
    if (parts.length === 1) {
      state = { ...state, [parts[0]]: value }
    } else if (parts.length === 2) {
      state = { ...state, [parts[0]]: { ...state[parts[0]], [parts[1]]: value } }
    } else if (parts.length === 3) {
      state = {
        ...state,
        [parts[0]]: {
          ...state[parts[0]],
          [parts[1]]: { ...state[parts[0]][parts[1]], [parts[2]]: value },
        },
      }
    } else {
      throw new Error(`Unsupported path: ${path}`)
    }
    notify()
  }

  function coloursForFabric(fabricType) {
    if (!fabricType) return []
    const wantCorduroy = fabricType === 'corduroy'
    return config.fabricColours.filter(c => !!c.corduroy === wantCorduroy).map(c => c.key)
  }

  function pickFabricType(fabricType) {
    const allowed = new Set(coloursForFabric(fabricType))

    // --- Corduroy-specific type constraints (applied on all paths) ---
    const nextArmrestType = fabricType === 'corduroy' && state.armrest.type === 'half-trapeziums'
      ? null : state.armrest.type
    const nextExtraFunType = fabricType === 'corduroy' && state.extraFun.type === 'half-trapeziums'
      ? null : state.extraFun.type

    // --- Snapshot current fabric's colours before switching ---
    const currentFabric = state.fabricType
    const saved = state._saved || {}
    if (currentFabric) {
      saved[currentFabric] = {
        upperBase: state.upperBase,
        lowerBase: state.lowerBase,
        trapezium: state.trapezium,
        armrestType: state.armrest.type,
        armrestColour: state.armrest.colour,
        extraFunType: state.extraFun.type,
        extraFunColour: state.extraFun.colour,
        cushion1fabric: state.dotCushion.cushion1.fabric,
        cushion2fabric: state.dotCushion.cushion2.fabric,
        baseThickness: state.baseThickness,
      }
    }

    let nextUpperBase, nextLowerBase, nextTrapezium, nextArmrestColour, nextExtraFunColour
    let nextCushion1Fabric, nextCushion2Fabric, nextBaseThickness

    if (saved[fabricType]) {
      // Branch A: restore snapshot, filter each colour through new palette
      const snap = saved[fabricType]
      const filterColour = (c) => (allowed.has(c) ? c : null)
      nextUpperBase = filterColour(snap.upperBase)
      nextLowerBase = filterColour(snap.lowerBase)
      nextTrapezium = filterColour(snap.trapezium)
      nextArmrestColour = filterColour(snap.armrestColour)
      nextExtraFunColour = filterColour(snap.extraFunColour)
      nextCushion1Fabric = filterColour(snap.cushion1fabric)
      nextCushion2Fabric = filterColour(snap.cushion2fabric)
      nextBaseThickness = snap.baseThickness
      // Restore armrest/extraFun types from snapshot (override the constraint-computed values)
      // Then re-apply corduroy constraints in case this restore is to corduroy
      const restoredArmrestType = snap.armrestType
      const restoredExtraFunType = snap.extraFunType
      const constrainedArmrestType = fabricType === 'corduroy' && restoredArmrestType === 'half-trapeziums'
        ? null : restoredArmrestType
      const constrainedExtraFunType = fabricType === 'corduroy' && restoredExtraFunType === 'half-trapeziums'
        ? null : restoredExtraFunType

      // Apply corduroy baseThickness constraint
      if (fabricType === 'corduroy' && nextBaseThickness === 'thin-and-thick') {
        nextBaseThickness = 'double-thick'
      }

      state = {
        ...state,
        _saved: saved,
        fabricType,
        baseThickness: nextBaseThickness,
        upperBase: nextUpperBase,
        lowerBase: nextLowerBase,
        trapezium: nextTrapezium,
        armrest: { type: constrainedArmrestType, colour: nextArmrestColour },
        extraFun: { type: constrainedExtraFunType, colour: nextExtraFunColour },
        dotCushion: {
          ...state.dotCushion,
          cushion1: { ...state.dotCushion.cushion1, fabric: nextCushion1Fabric },
          cushion2: { ...state.dotCushion.cushion2, fabric: nextCushion2Fabric },
        },
      }
      notify()
      return
    } else if (fabricType === 'corduroy') {
      // Branch B: first time switching to corduroy — default to sage-green (filtered to null if not in palette)
      const sageGreen = allowed.has('sage-green') ? 'sage-green' : null
      nextUpperBase = sageGreen
      nextLowerBase = sageGreen
      nextTrapezium = sageGreen
      nextArmrestColour = sageGreen
      nextExtraFunColour = sageGreen
      nextCushion1Fabric = sageGreen
      nextCushion2Fabric = sageGreen
      nextBaseThickness = 'double-thick'
    } else if (fabricType === 'microsuede') {
      // Branch B2: first time switching to microsuede — default to sky-blue
      // everywhere so pricing is populated immediately and the 3D mesh doesn't
      // keep stale corduroy tint. User's own selections are restored in
      // branch A if they've visited microsuede before.
      const skyBlue = allowed.has('sky-blue') ? 'sky-blue' : null
      nextUpperBase = skyBlue
      nextLowerBase = skyBlue
      nextTrapezium = skyBlue
      nextArmrestColour = skyBlue
      nextExtraFunColour = skyBlue
      nextCushion1Fabric = skyBlue
      nextCushion2Fabric = skyBlue
      nextBaseThickness = state.baseThickness || 'double-thick'
    } else {
      // Branch C: any other fabric — clear invalid colours (legacy safety path)
      const clearIfInvalid = (key) => (allowed.has(state[key]) ? state[key] : null)
      nextUpperBase = clearIfInvalid('upperBase')
      nextLowerBase = clearIfInvalid('lowerBase')
      nextTrapezium = clearIfInvalid('trapezium')
      nextArmrestColour = allowed.has(state.armrest.colour) ? state.armrest.colour : null
      nextExtraFunColour = allowed.has(state.extraFun.colour) ? state.extraFun.colour : null
      nextCushion1Fabric = allowed.has(state.dotCushion.cushion1.fabric) ? state.dotCushion.cushion1.fabric : null
      nextCushion2Fabric = allowed.has(state.dotCushion.cushion2.fabric) ? state.dotCushion.cushion2.fabric : null
      nextBaseThickness = state.baseThickness
    }

    // Apply corduroy baseThickness constraint (Branches B and C)
    if (fabricType === 'corduroy' && nextBaseThickness === 'thin-and-thick') {
      nextBaseThickness = 'double-thick'
    }

    state = {
      ...state,
      _saved: saved,
      fabricType,
      baseThickness: nextBaseThickness,
      upperBase: nextUpperBase,
      lowerBase: nextLowerBase,
      trapezium: nextTrapezium,
      armrest: { type: nextArmrestType, colour: nextArmrestColour },
      extraFun: { type: nextExtraFunType, colour: nextExtraFunColour },
      dotCushion: {
        ...state.dotCushion,
        cushion1: { ...state.dotCushion.cushion1, fabric: nextCushion1Fabric },
        cushion2: { ...state.dotCushion.cushion2, fabric: nextCushion2Fabric },
      },
    }
    notify()
  }

  function setRegionColour(region, colour) {
    if (region === 'armrest' || region === 'extraFun') {
      set(`${region}.colour`, colour)
    } else if (region === 'lowerBase' || region === 'trapezium') {
      set(region, colour)
    } else if (region === 'upperBase') {
      set('upperBase', colour)
    } else {
      throw new Error(`Unknown region: ${region}`)
    }
  }

  function validateText(text) {
    if (text.length < config.text.min) return { ok: false, reason: 'too_short' }
    if (text.length > config.text.max) return { ok: false, reason: 'too_long' }
    return { ok: true }
  }

  function isCushionComplete(cushion) {
    if (!cushion.fabric) return false
    if (cushion.wantsText === null) return false
    if (cushion.wantsText === true) {
      if (!validateText(cushion.text).ok) return false
      if (!cushion.font) return false
      if (!cushion.textColour) return false
    }
    return true
  }

  function isComplete() {
    if (!state.fabricType) return false
    if (!state.baseThickness) return false
    if (!state.upperBase || !state.lowerBase || !state.trapezium) return false
    if (!state.armrest.type) return false
    if (state.armrest.type !== 'none' && !state.armrest.colour) return false
    if (!state.extraFun.type) return false
    if (state.extraFun.type !== 'none' && !state.extraFun.colour) return false
    if (state.dotCushion.enabled === null) return false
    if (state.dotCushion.enabled === true) {
      if (!state.dotCushion.cushion1.fabric) return false
      if (!state.dotCushion.cushion2.fabric) return false
      if (state.fabricType === 'microsuede') {
        if (!isCushionComplete(state.dotCushion.cushion1)) return false
        if (!isCushionComplete(state.dotCushion.cushion2)) return false
      }
    }
    return true
  }

  // RESET_DEFAULTS_START
  function reset() {
    const fresh = createStore(config)
    fresh.pickFabricType(state.fabricType || 'microsuede')
    setState(fresh.getState())
  }
  // RESET_DEFAULTS_END
  function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) }
  function getState() { return state }
  function setState(next) { state = next; notify() }

  return {
    getState, setState, set, reset,
    pickFabricType, setRegionColour,
    validateText, isComplete, subscribe,
    _coloursForFabric: coloursForFabric,
  }
}

export function isTextAvailableForCushion(cushion, config, state) {
  if (!state || state.fabricType !== 'microsuede') return false
  if (!cushion || !cushion.fabric) return false
  return true
}

const BASE_THICKNESS_VARIANT_PREFIX = {
  'thin-and-thick': 'thin',
  'double-thick': 'thick',
}

function primaryVariantId(colour, componentKey, config) {
  if (!colour) return null
  return config.components[componentKey].variants[colour] || null
}

function upperBaseVariantId(state, config) {
  if (!state.upperBase || !state.baseThickness) return null
  const prefix = BASE_THICKNESS_VARIANT_PREFIX[state.baseThickness] ?? state.baseThickness
  return config.components.upperBase.variants[`${prefix}:${state.upperBase}`] || null
}

function armrestVariantId(armrest, config) {
  if (!armrest.type || armrest.type === 'none' || !armrest.colour) return null
  return config.components.armrest.variants[`${armrest.type}:${armrest.colour}`] || null
}

function extraFunVariantId(extraFun, config) {
  if (!extraFun.type || extraFun.type === 'none' || !extraFun.colour) return null
  if (!config.components.extraFun?.variants) return null
  return config.components.extraFun.variants[`${extraFun.type}:${extraFun.colour}`] || null
}

function cushionVariantId(cushion, config) {
  if (!cushion.fabric) return null
  return config.components.dotCushion.variants[cushion.fabric] || null
}

export function getLineItems(state, config) {
  const lines = []

  const upperVid = upperBaseVariantId(state, config)
  if (upperVid) {
    lines.push({ componentKey: 'upperBase', variantId: upperVid, quantity: 1, properties: {} })
  }

  const secondaryPrimaries = [
    ['lowerBase', state.lowerBase],
    ['trapezium', state.trapezium],
  ]
  for (const [key, colour] of secondaryPrimaries) {
    const vid = primaryVariantId(colour, key, config)
    if (!vid) continue
    lines.push({ componentKey: key, variantId: vid, quantity: 1, properties: {} })
  }

  const armrestVid = armrestVariantId(state.armrest, config)
  if (armrestVid) {
    lines.push({ componentKey: 'armrest', variantId: armrestVid, quantity: 1, properties: {} })
  }

  const extraFunVid = state.extraFun ? extraFunVariantId(state.extraFun, config) : null
  if (extraFunVid) {
    lines.push({ componentKey: 'extraFun', variantId: extraFunVid, quantity: 1, properties: {} })
  }

  if (state.dotCushion.enabled === true) {
    const cushions = [
      [1, state.dotCushion.cushion1],
      [2, state.dotCushion.cushion2],
    ]
    for (const [idx, cushion] of cushions) {
      const vid = cushionVariantId(cushion, config)
      if (!vid) continue
      const shouldEmitText = state.fabricType === 'microsuede' && cushion.wantsText === true
      const properties = { cushion: idx }
      if (shouldEmitText) {
        properties.text = cushion.text
        properties.font = cushion.font
        properties.textColour = cushion.textColour
      }
      lines.push({
        componentKey: 'dotCushion',
        variantId: vid,
        quantity: 1,
        cushionIndex: idx,
        properties,
      })
      if (shouldEmitText) {
        const textVid = config.components.dotText.variants.yes
        if (textVid) {
          lines.push({
            componentKey: 'dotText',
            variantId: textVid,
            quantity: 1,
            cushionIndex: idx,
            properties: {},
          })
        }
      }
    }
  }

  return lines
}

// Walks the resolved line-items for the current state and returns an array
// describing every selected variant that is currently OOS. Used to:
//  - block ATC (we shouldn't take the order if Shopify will reject it)
//  - render a single inline notice near ATC explaining the blocker
// Returns [] when every selected variant is available (or when variantData is
// empty — defensive fail-open so a config gap never traps the customer).
export function unavailableSelections(state, config, variantData) {
  if (!variantData) return []
  const out = []
  for (const line of getLineItems(state, config)) {
    const v = variantData[line.variantId]
    if (v && v.available === false) {
      out.push({ componentKey: line.componentKey, variantId: line.variantId })
    }
  }
  return out
}

export function computePricing(state, config, variantData) {
  let regular = 0
  let compare = 0
  const includedVariantIds = []

  for (const line of getLineItems(state, config)) {
    const v = variantData[line.variantId]
    if (!v) continue
    regular += v.price * line.quantity
    compare += (v.compare_at_price ?? v.price) * line.quantity
    includedVariantIds.push(line.variantId)
  }

  return {
    regular,
    compare,
    onSale: compare > regular,
    savings: Math.max(0, compare - regular),
    includedVariantIds,
  }
}

export function serializeState(state) {
  // Strip session-only _saved field — it must not appear in the URL
  const { _saved: _omit, ...persistable } = state
  const json = JSON.stringify(persistable)
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(json)))
  return Buffer.from(json, 'utf8').toString('base64')
}

export function deserializeState(encoded) {
  if (!encoded || typeof encoded !== 'string') return null
  try {
    const json = typeof atob === 'function'
      ? decodeURIComponent(escape(atob(encoded)))
      : Buffer.from(encoded, 'base64').toString('utf8')
    const parsed = JSON.parse(json)
    return {
      fabricType: parsed.fabricType ?? null,
      baseThickness: parsed.baseThickness ?? null,
      upperBase: parsed.upperBase ?? null,
      lowerBase: parsed.lowerBase ?? null,
      trapezium: parsed.trapezium ?? null,
      armrest: {
        type: parsed.armrest?.type ?? null,
        colour: parsed.armrest?.colour ?? null,
      },
      extraFun: {
        type: parsed.extraFun?.type ?? null,
        colour: parsed.extraFun?.colour ?? null,
      },
      dotCushion: {
        enabled: parsed.dotCushion?.enabled ?? null,
        cushion1: { ...EMPTY_CUSHION, ...(parsed.dotCushion?.cushion1 ?? {}) },
        cushion2: { ...EMPTY_CUSHION, ...(parsed.dotCushion?.cushion2 ?? {}) },
      },
    }
  } catch {
    return null
  }
}

// ============ DOM wiring (runs in browser only) ============

function escapeAttr(value) {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

// ============ Step definitions + per-step renderers (Task 33a) ============
//
// STEP_DEFS is the single source of truth for the stepped UX — both the
// mobile stepper and the desktop accordion iterate over it. `showIf` lets
// a step hide based on current state (e.g. armrest colour only shows once
// an armrest type is picked). `renderStep(id, state, store, config, mount)`
// writes the body HTML for one step into the supplied mount element.
//
// Task 33b will wire the arrow navigator and accordion around these.

export const STEP_DEFS = [
  { id: 'fabricType',     title: 'Choose Fabric Type' },
  { id: 'baseThickness',  title: 'Base Thickness' },
  { id: 'upperBase',      title: 'Upper Base' },
  { id: 'lowerBase',      title: 'Lower Base' },
  { id: 'trapezium',      title: 'Trapezium' },
  { id: 'armrestType',    title: 'Choose Your Armrest' },
  { id: 'armrestColour',  title: 'Armrest Color',   showIf: s => s.armrest.type && s.armrest.type !== 'none' },
  { id: 'extraFunType',      title: 'Extra Fun' },
  { id: 'extraFunColour',    title: 'Extra Fun Color',        showIf: s => s.extraFun.type && s.extraFun.type !== 'none' },
  { id: 'dotCushionChoice',  title: 'Dot Cushions' },
  { id: 'dotCushion1Colour', title: '1st Dot Colour',         showIf: s => s.dotCushion.enabled === true },
  { id: 'dotCushion2Colour', title: '2nd Dot Colour',         showIf: s => s.dotCushion.enabled === true },
  { id: 'dotCushion1',       title: '1st Dot text (Microsuede only)', showIf: s => s.dotCushion.enabled === true && s.fabricType === 'microsuede' },
  { id: 'dotCushion2',       title: '2nd Dot text (Microsuede only)', showIf: s => s.dotCushion.enabled === true && s.fabricType === 'microsuede' },
  // T48.8: mobile-only summary page. Rendered as the last step in the mobile
  // stepper arrow flow; omitted from the desktop accordion via the
  // `mobileOnly: true` flag.
  { id: 'summary',           title: 'Summary', mobileOnly: true },
]

export function visibleSteps(state) {
  return STEP_DEFS.filter(d => !d.showIf || d.showIf(state))
}

export function visibleDesktopSteps(state) {
  return visibleSteps(state).filter(d => !d.mobileOnly)
}

function fabricTypeStepHtml(state, config) {
  const swatchImages = (config && config.fabricTypeSwatches) || {}
  const swatch = (key, label) => {
    const img = swatchImages[key]
    const bgStyle = img ? `background-image:url('${escapeAttr(img)}');background-size:cover;background-position:center;` : ''
    return `<button class="fabric-swatch ${state.fabricType === key ? 'is-selected' : ''}"
       data-fabric-type="${key}" aria-label="${escapeAttr(label)}" title="${escapeAttr(label)}"
       style="${bgStyle}"
     ></button>`
  }
  return `
    <div class="fabric-swatches">
      ${swatch('microsuede', 'Microsuede')}
      ${swatch('corduroy',   'Corduroy')}
    </div>
  `
}

function baseThicknessStepHtml(state, config) {
  const options = state.fabricType === 'corduroy'
    ? config.baseThicknesses.filter(t => t.key !== 'thin-and-thick')
    : config.baseThicknesses
  return options.map(t => `<button class="choice ${state.baseThickness === t.key ? 'is-selected' : ''}"
      data-base-thickness="${escapeAttr(t.key)}">${escapeAttr(t.label)}</button>`).join('')
}

// Returns the variant id a region swatch for `colourKey` would resolve to in the
// current state. Used to check variant availability when rendering swatches.
// Returns null if the variant can't be resolved yet (e.g. baseThickness not set).
function regionSwatchVariantId(region, colourKey, state, config) {
  if (region === 'upperBase') {
    if (!state.baseThickness) return null
    const prefix = BASE_THICKNESS_VARIANT_PREFIX[state.baseThickness] ?? state.baseThickness
    return config.components.upperBase.variants[`${prefix}:${colourKey}`] || null
  }
  if (region === 'lowerBase' || region === 'trapezium') {
    return config.components[region].variants[colourKey] || null
  }
  if (region === 'armrest') {
    if (!state.armrest.type || state.armrest.type === 'none') return null
    return config.components.armrest.variants[`${state.armrest.type}:${colourKey}`] || null
  }
  if (region === 'extraFun') {
    if (!state.extraFun.type || state.extraFun.type === 'none') return null
    return config.components.extraFun?.variants[`${state.extraFun.type}:${colourKey}`] || null
  }
  return null
}

// Given a variant id and variantData, returns true if the variant is available
// for purchase. Unknown / missing variants default to available (fail-open) so
// a config gap never blocks the UI.
function isVariantAvailable(vid, variantData) {
  if (!vid) return true
  const v = variantData && variantData[vid]
  if (!v) return true
  return v.available !== false
}

// Renders the OOS badge inside a swatch button. Kept as a single line so the
// circle styling stays clean and the label is announced to screen readers.
function oosBadgeHtml() {
  return `<span class="swatch__oos-badge" aria-hidden="true"></span>`
}

function regionColourStepHtml(state, config, region, variantData) {
  const vd = variantData || {}
  const allowed = config.fabricColours.filter(c =>
    state.fabricType === 'corduroy' ? !!c.corduroy : !c.corduroy
  )
  const selectedKey = region === 'armrest' ? state.armrest.colour
                    : region === 'extraFun' ? state.extraFun.colour
                    : state[region]
  // OOS swatches stay clickable (preview-only) — the colour applies to the model
  // so the customer can still see what they would have got. ATC is blocked
  // separately via hasUnavailableSelection() so the order can't actually go
  // through with an OOS variant. The post-click "_Colour_ — Out of Stock" notice
  // below the swatches is the customer-facing signal that the choice can't be
  // purchased.
  let selectedOosLabel = null
  const swatchHtml = allowed.map(c => {
    const vid = regionSwatchVariantId(region, c.key, state, config)
    const available = isVariantAvailable(vid, vd)
    const selected = selectedKey === c.key
    if (!available && selected) selectedOosLabel = c.label
    const classes = ['swatch']
    if (selected) classes.push('is-selected')
    if (!available) classes.push('is-unavailable')
    return `
      <button class="${classes.join(' ')}"
        data-region-colour="${escapeAttr(region)}"
        data-colour="${escapeAttr(c.key)}"
        style="background:${escapeAttr(c.hex)}"
        title="${escapeAttr(available ? c.label : c.label + ' — Out of Stock')}"
        aria-label="${escapeAttr(available ? c.label : c.label + ', out of stock')}">${available ? '' : oosBadgeHtml()}</button>
    `
  }).join('')
  // Only show a notice once the customer has actually clicked an OOS swatch
  // (i.e. it is the selected colour for this region). The notice is intentionally
  // singular so the swatch grid stays uncluttered until that moment.
  const oosNotice = selectedOosLabel === null ? '' : `
    <div class="swatches__oos-notice" role="status" aria-live="polite">
      <span class="oos-pill"><strong>${escapeAttr(selectedOosLabel)}</strong> — Out of Stock</span>
    </div>
  `
  return `
    <div class="swatches">${swatchHtml}</div>
    ${oosNotice}
  `
}

// Anchor-colour price lookup for an option-type button (e.g. "Cuboid", "Folding
// Circle"). Reads the variant priced at config.priceAnchorColour for the given
// type; if that variant is OOS or missing, falls back to the first available
// variant of the same type. Returns null when no variant of the type is
// available — in which case the caller should render no "+$X" suffix at all.
export function optionTypePricing(typeKey, componentKey, config, variantData) {
  if (!variantData) return null
  const component = config.components && config.components[componentKey]
  if (!component) return null
  const variants = component.variants || {}
  const isAvailable = (v) => v && v.available !== false && typeof v.price === 'number'

  const anchor = config.priceAnchorColour
  if (anchor) {
    const anchorKey = `${typeKey}:${anchor}`
    if (variants[anchorKey] != null) {
      const v = variantData[variants[anchorKey]]
      if (isAvailable(v)) return { price: v.price, compareAtPrice: v.compare_at_price ?? null }
    }
  }

  const prefix = `${typeKey}:`
  for (const key of Object.keys(variants)) {
    if (!key.startsWith(prefix)) continue
    const v = variantData[variants[key]]
    if (isAvailable(v)) return { price: v.price, compareAtPrice: v.compare_at_price ?? null }
  }
  return null
}

// Renders the dynamic price suffix appended to an option-type label.
// Returns empty string when pricing is null or the type costs nothing.
function optionPriceSuffixHtml(pricing) {
  if (!pricing || !pricing.price) return ''
  const fmt = (cents) => `+$${(cents / 100).toFixed(0)}`
  if (pricing.compareAtPrice && pricing.compareAtPrice > pricing.price) {
    return ` <s class="choice-was">${fmt(pricing.compareAtPrice)}</s> ${fmt(pricing.price)}`
  }
  return ` ${fmt(pricing.price)}`
}

function armrestTypeStepHtml(state, config, variantData) {
  const isCorduroy = state.fabricType === 'corduroy'
  return config.armrestTypes
    .filter(t => !(isCorduroy && t.corduroyAvailable === false))
    .map(t => {
      const suffix = optionPriceSuffixHtml(optionTypePricing(t.key, 'armrest', config, variantData))
      return `<button class="choice ${state.armrest.type === t.key ? 'is-selected' : ''}"
      data-armrest-type="${escapeAttr(t.key)}">${escapeAttr(t.label)}${suffix}</button>`
    })
    .join('')
}

function extraFunTypeStepHtml(state, config, variantData) {
  const isCorduroy = state.fabricType === 'corduroy'
  return config.extraFunTypes
    .filter(t => !(isCorduroy && t.corduroyAvailable === false))
    .map(t => {
      const suffix = optionPriceSuffixHtml(optionTypePricing(t.key, 'extraFun', config, variantData))
      return `<button class="choice ${state.extraFun.type === t.key ? 'is-selected' : ''}"
      data-extrafun-type="${escapeAttr(t.key)}">${escapeAttr(t.label)}${suffix}</button>`
    })
    .join('')
}

// Anchor-colour pricing lookup for components keyed by plain colour (no
// `<typeKey>:` prefix) — currently dotCushion. Returns { price, compareAtPrice }
// in cents for the anchor variant, falling back to the first available variant
// if the anchor is OOS or missing. Returns null if no variant is available.
export function componentAnchorPricing(componentKey, config, variantData) {
  if (!variantData) return null
  const component = config.components && config.components[componentKey]
  if (!component) return null
  const variants = component.variants || {}
  const isAvailable = (v) => v && v.available !== false && typeof v.price === 'number'

  const anchor = config.priceAnchorColour
  if (anchor && variants[anchor] != null) {
    const v = variantData[variants[anchor]]
    if (isAvailable(v)) return { price: v.price, compareAtPrice: v.compare_at_price ?? null }
  }
  for (const key of Object.keys(variants)) {
    const v = variantData[variants[key]]
    if (isAvailable(v)) return { price: v.price, compareAtPrice: v.compare_at_price ?? null }
  }
  return null
}

// Returns the unit price (in cents) for one dot cushion. Kept for back-compat
// with existing callers; new code should use componentAnchorPricing directly
// to also access compare_at_price for sale rendering.
export function dotCushionUnitPrice(config, variantData) {
  return componentAnchorPricing('dotCushion', config, variantData)?.price ?? 0
}

function dotCushionChoiceStepHtml(state, config, variantData) {
  const pricing = componentAnchorPricing('dotCushion', config, variantData)
  const fmt = (cents) => `+$${(cents / 100).toFixed(0)}`
  let totalStr = '+$?'
  if (pricing && pricing.price) {
    if (pricing.compareAtPrice && pricing.compareAtPrice > pricing.price) {
      totalStr = `<s class="choice-was">${fmt(pricing.compareAtPrice * 2)}</s> ${fmt(pricing.price * 2)}`
    } else {
      totalStr = fmt(pricing.price * 2)
    }
  }
  const noSelected = state.dotCushion.enabled === false
  const yesSelected = state.dotCushion.enabled === true
  return `
    <div class="tiles">
      <button class="tile ${noSelected ? 'is-selected' : ''}"
        data-dotcushion-enabled="false">No Thank You</button>
      <button class="tile ${yesSelected ? 'is-selected' : ''}"
        data-dotcushion-enabled="true">Yes Please ${totalStr}</button>
    </div>
  `
}

function cushionColourStepHtml(state, config, cushionIdx, variantData) {
  const vd = variantData || {}
  const allowed = config.fabricColours.filter(c =>
    state.fabricType === 'corduroy' ? !!c.corduroy : !c.corduroy
  )
  const cushion = state.dotCushion[`cushion${cushionIdx}`]
  const selectedKey = cushion ? cushion.fabric : null
  let selectedOosLabel = null
  const swatchHtml = allowed.map(c => {
    const vid = config.components.dotCushion.variants[c.key] || null
    const available = isVariantAvailable(vid, vd)
    const selected = selectedKey === c.key
    if (!available && selected) selectedOosLabel = c.label
    const classes = ['swatch']
    if (selected) classes.push('is-selected')
    if (!available) classes.push('is-unavailable')
    return `
      <button class="${classes.join(' ')}"
        data-cushion${cushionIdx}-colour="${escapeAttr(c.key)}"
        style="background:${escapeAttr(c.hex)}"
        title="${escapeAttr(available ? c.label : c.label + ' — Out of Stock')}"
        aria-label="${escapeAttr(available ? c.label : c.label + ', out of stock')}">${available ? '' : oosBadgeHtml()}</button>
    `
  }).join('')
  const oosNotice = selectedOosLabel === null ? '' : `
    <div class="swatches__oos-notice" role="status" aria-live="polite">
      <span class="oos-pill"><strong>${escapeAttr(selectedOosLabel)}</strong> — Out of Stock</span>
    </div>
  `
  return `
    <div class="swatches">${swatchHtml}</div>
    ${oosNotice}
  `
}

// Reads the dot-cushion-text "yes" variant pricing from variantData. Returns
// { price, compareAtPrice } in cents, or null if missing/unavailable.
// Unlike dotCushionUnitPrice (colour-keyed), dotText is keyed by yes/no — so
// we look up the yes variant directly rather than via the anchor-colour helper.
export function dotTextUnitPricing(config, variantData) {
  if (!variantData || !config.components || !config.components.dotText) return null
  const variants = config.components.dotText.variants || {}
  const yesVid = variants.yes
  if (yesVid == null) return null
  const v = variantData[yesVid]
  if (!v || v.available === false || typeof v.price !== 'number') return null
  return { price: v.price, compareAtPrice: v.compare_at_price ?? null }
}

// <option> tags don't support HTML markup, so the dot-cushion-text dropdown
// can't use <s> for strikethrough. Instead we use Unicode combining U+0336
// (combining long stroke overlay) — each char of the "was" price gets the
// combining mark appended, rendering as struck-through plain text.
function strikeThroughText(s) {
  let out = ''
  for (const ch of String(s)) out += ch + '̶'
  return out
}

function dotCushionStepHtml(state, config, idx, variantData) {
  const cushion = state.dotCushion[`cushion${idx}`]
  const wants = cushion.wantsText
  const enabled = state.fabricType === 'microsuede'
  if (!enabled) {
    return `<p class="hint">Dot text is available on Microsuede fabrics only.</p>`
  }
  // Build the "Yes Please" label dynamically: pull current price from the
  // dot-text yes variant; if compare_at_price > price, render the was-price
  // in parentheses with Unicode strikethrough, then the new price.
  const dotTextPricing = dotTextUnitPricing(config, variantData)
  const fmt = (cents) => `+$${(cents / 100).toFixed(0)}`
  let yesLabel = 'Yes Please'
  if (dotTextPricing && dotTextPricing.price > 0) {
    if (dotTextPricing.compareAtPrice && dotTextPricing.compareAtPrice > dotTextPricing.price) {
      const wasStruck = strikeThroughText(`(${fmt(dotTextPricing.compareAtPrice)})`)
      yesLabel = `Yes Please ${wasStruck} ${fmt(dotTextPricing.price)}`
    } else {
      yesLabel = `Yes Please ${fmt(dotTextPricing.price)}`
    }
  }
  const dropdown = `
    <select class="dot-dropdown" data-cushion${idx}-wants-text>
      <option value="" ${wants === null ? 'selected' : ''}>Select</option>
      <option value="false" ${wants === false ? 'selected' : ''}>No Thank You</option>
      <option value="true"  ${wants === true  ? 'selected' : ''}>${yesLabel}</option>
    </select>
  `
  if (wants !== true) return dropdown

  const textInput = `
    <label>Custom text:
      <input type="text" data-cushion${idx}-text-input
        value="${escapeAttr(cushion.text || '')}"
        minlength="${config.text.min}" maxlength="${config.text.max}"
        placeholder="${config.text.min}–${config.text.max} characters">
    </label>
  `
  const fontSelect = `
    <label>Font:
      <select data-cushion${idx}-font>
        <option value="" ${!cushion.font ? 'selected' : ''}>Select</option>
        ${config.dotFonts.map(f =>
          `<option value="${escapeAttr(f.key)}" ${cushion.font === f.key ? 'selected' : ''}>${escapeAttr(f.label)}</option>`
        ).join('')}
      </select>
    </label>
  `
  const colourSwatches = `
    <label>Color</label>
    <div class="swatches">
      ${config.dotTextColours.map(c => `
        <button class="swatch ${cushion.textColour === c.key ? 'is-selected' : ''}"
          data-cushion${idx}-text-colour="${escapeAttr(c.key)}"
          style="background:${escapeAttr(c.hex)}"
          title="${escapeAttr(c.label)}"></button>
      `).join('')}
    </div>
  `
  return dropdown + textInput + fontSelect + colourSwatches
}

// T48.8: mobile-only summary page. Iterates every visible step (except this one)
// and renders Label | Value | ✏️. The pencil carries data-edit-step="<id>"
// which the renderSteppedShell click handler turns into `nav.goTo(id)` + re-render.
function summaryStepHtml(state, config) {
  const rows = visibleSteps(state).filter(d => d.id !== 'summary')
  return `
    <ul class="summary-list">
      ${rows.map(step => {
        const valueRaw = summariseStepSelection(step.id, state, config)
        const value = valueRaw == null || valueRaw === '' ? '—' : valueRaw
        return `
          <li class="summary-row">
            <span class="summary-row__label">${escapeAttr(step.title)}</span>
            <span class="summary-row__value">${escapeAttr(value)}</span>
            <button class="summary-row__edit" type="button" data-edit-step="${escapeAttr(step.id)}" aria-label="Edit ${escapeAttr(step.title)}">&#9998;</button>
          </li>
        `
      }).join('')}
    </ul>
  `
}

export function renderStep(stepId, state, store, config, mount, variantData) {
  const vd = variantData || {}
  let body = ''
  switch (stepId) {
    case 'fabricType':        body = fabricTypeStepHtml(state, config); break
    case 'baseThickness':     body = baseThicknessStepHtml(state, config); break
    case 'upperBase':         body = regionColourStepHtml(state, config, 'upperBase', vd); break
    case 'lowerBase':         body = regionColourStepHtml(state, config, 'lowerBase', vd); break
    case 'trapezium':         body = regionColourStepHtml(state, config, 'trapezium', vd); break
    case 'armrestType':       body = armrestTypeStepHtml(state, config, vd); break
    case 'armrestColour':     body = regionColourStepHtml(state, config, 'armrest', vd); break
    case 'extraFunType':      body = extraFunTypeStepHtml(state, config, vd); break
    case 'extraFunColour':    body = regionColourStepHtml(state, config, 'extraFun', vd); break
    case 'dotCushionChoice':  body = dotCushionChoiceStepHtml(state, config, vd); break
    case 'dotCushion1Colour': body = cushionColourStepHtml(state, config, 1, vd); break
    case 'dotCushion2Colour': body = cushionColourStepHtml(state, config, 2, vd); break
    case 'dotCushion1':       body = dotCushionStepHtml(state, config, 1, vd); break
    case 'dotCushion2':       body = dotCushionStepHtml(state, config, 2, vd); break
    case 'summary':           body = summaryStepHtml(state, config); break
    default: body = ''
  }
  mount.innerHTML = body
}

// ============ Stepper navigation (Task 33b) ============
//
// createStepperNav(store) tracks a "current step" index into visibleSteps(state).
// onStateChange() clamps the index if steps have disappeared underneath us
// (e.g. armrestColour goes away when user picks armrest.type=none).

export function createStepperNav(store) {
  let idx = 0
  function visible() { return visibleSteps(store.getState()) }
  return {
    current() { return visible()[idx] ?? null },
    next() {
      const v = visible()
      idx = Math.min(idx + 1, v.length - 1)
    },
    prev() {
      idx = Math.max(idx - 1, 0)
    },
    goTo(id) {
      const v = visible()
      const i = v.findIndex(s => s.id === id)
      if (i >= 0) idx = i
    },
    atStart() { return idx === 0 },
    atEnd() { const v = visible(); return idx === v.length - 1 },
    onStateChange() {
      const v = visible()
      if (idx >= v.length) idx = v.length - 1
    },
  }
}

function summariseStepSelection(stepId, state, config) {
  const label = (arr, key) => arr.find(x => x.key === key)?.label
  switch (stepId) {
    case 'fabricType':     return state.fabricType ? (state.fabricType === 'corduroy' ? 'Corduroy' : 'Microsuede') : null
    case 'baseThickness':  return label(config.baseThicknesses, state.baseThickness) ?? null
    case 'upperBase':      return label(config.fabricColours, state.upperBase) ?? null
    case 'lowerBase':      return label(config.fabricColours, state.lowerBase) ?? null
    case 'trapezium':      return label(config.fabricColours, state.trapezium) ?? null
    case 'armrestType':    return label(config.armrestTypes, state.armrest.type) ?? null
    case 'armrestColour':  return label(config.fabricColours, state.armrest.colour) ?? null
    case 'extraFunType':   return label(config.extraFunTypes, state.extraFun.type) ?? null
    case 'extraFunColour':    return label(config.fabricColours, state.extraFun.colour) ?? null
    case 'dotCushionChoice':  return state.dotCushion.enabled === true ? 'Yes' : state.dotCushion.enabled === false ? 'No' : null
    case 'dotCushion1Colour': return label(config.fabricColours, state.dotCushion.cushion1.fabric) ?? null
    case 'dotCushion2Colour': return label(config.fabricColours, state.dotCushion.cushion2.fabric) ?? null
    case 'dotCushion1':    return state.dotCushion.cushion1.wantsText === true ? `"${state.dotCushion.cushion1.text || ''}"` : (state.dotCushion.cushion1.wantsText === false ? 'No' : null)
    case 'dotCushion2':    return state.dotCushion.cushion2.wantsText === true ? `"${state.dotCushion.cushion2.text || ''}"` : (state.dotCushion.cushion2.wantsText === false ? 'No' : null)
    default: return null
  }
}

export function renderSteppedShell(root, store, config, nav, variantData) {
  window.__ccShopping?.beforeStepsRender()
  const container = root.querySelector('[data-steps]')
  const state = store.getState()
  const steps = visibleSteps(state)
  const current = nav.current()

  // Preserve focus + caret across the innerHTML rebuild below. Without this,
  // each keystroke in the custom-text input loses focus (store.set triggers
  // rerender, which wipes the input and recreates it). We identify the active
  // element by a stable data-* attribute, then re-focus the new one.
  const activeEl = document.activeElement
  let focusRestore = null
  if (container && activeEl && container.contains(activeEl) && activeEl.tagName === 'INPUT') {
    const attrName = Array.from(activeEl.attributes)
      .map(a => a.name)
      .find(n => n.startsWith('data-cushion') && n.endsWith('-text-input'))
    if (attrName) {
      focusRestore = {
        attrName,
        selectionStart: activeEl.selectionStart,
        selectionEnd: activeEl.selectionEnd,
      }
    }
  }

  const stepTitleRow = (step) => {
    const selection = summariseStepSelection(step.id, state, config)
    return `
      <div class="stepper-title">
        <button class="stepper-arrow prev" data-stepper-prev aria-label="Previous step" ${nav.atStart() ? 'disabled' : ''}>
          <span class="stepper-arrow__glyph" aria-hidden="true">&lsaquo;</span>
          <span class="stepper-arrow__label">Previous<br>Step</span>
        </button>
        <div class="stepper-title-text">
          <h3>${step.title}</h3>
          ${selection ? `<p class="stepper-selection">${escapeAttr(selection)}</p>` : ''}
        </div>
        <button class="stepper-arrow next" data-stepper-next aria-label="Next step" ${nav.atEnd() ? 'disabled' : ''}>
          <span class="stepper-arrow__glyph" aria-hidden="true">&rsaquo;</span>
          <span class="stepper-arrow__label">Next<br>Step</span>
        </button>
      </div>
    `
  }

  container.innerHTML = `
    <nav class="cc-shop-progress" data-shop-progress aria-label="Customiser stages"></nav><div class="cc-shop-fabric-help" data-shop-fabric-help></div>
    <!-- Mobile: single step -->
    <div class="couch-customiser__stepper" data-stepper>
      ${current ? stepTitleRow(current) : ''}
      <div class="stepper-body" data-stepper-body></div>
    </div>
    <!-- Desktop: accordion with all steps -->
    <div class="couch-customiser__accordion" data-accordion>
      ${steps.filter(step => !step.mobileOnly).map(step => {
        const isOpen = current && current.id === step.id
        const summary = summariseStepSelection(step.id, state, config)

        // Always-visible steps (no pill, no chevron). Used for the top-of-panel
        // fabric picker and the base-thickness row. Matches LIVE.
        if (step.id === 'fabricType' || step.id === 'baseThickness') {
          return `
            <section class="accordion-step--flat" data-flat-step="${step.id}">
              <div class="accordion-step__title-inline">
                <span class="accordion-title">${step.title}</span>
                ${summary ? `<span class="accordion-summary-inline">${escapeAttr(summary)}</span>` : ''}
              </div>
              <div class="accordion-body" data-accordion-body="${step.id}"></div>
            </section>
          `
        }

        return `
          <details class="accordion-step" name="couch-customiser-accordion" ${isOpen ? 'open' : ''} data-accordion-step="${step.id}">
            <summary>
              <span class="accordion-title">${step.title}</span>
              ${summary ? `<span class="accordion-summary">${escapeAttr(summary)}</span>` : ''}
            </summary>
            <div class="accordion-body" data-accordion-body="${step.id}"></div>
          </details>
        `
      }).join('')}
    </div>
  `

  const mobileBody = container.querySelector('[data-stepper-body]')
  if (current && mobileBody) renderStep(current.id, state, store, config, mobileBody, variantData)
  for (const step of steps) {
    if (step.mobileOnly) continue
    const desktopBody = container.querySelector(`[data-accordion-body="${step.id}"]`)
    if (desktopBody) renderStep(step.id, state, store, config, desktopBody, variantData)
  }

  // Restore focus + caret on the rebuilt text input (see capture block above).
  // Desktop accordion + mobile stepper can both contain an input with the same
  // data-* attribute, so prefer one inside a <details open> or visible stepper.
  if (focusRestore) {
    const candidates = container.querySelectorAll(`input[${focusRestore.attrName}]`)
    let target = null
    for (const el of candidates) {
      if (el.offsetParent !== null) { target = el; break }  // first visible
    }
    target = target || candidates[0]
    if (target) {
      target.focus()
      const end = target.value.length
      const start = Math.min(focusRestore.selectionStart ?? end, end)
      const stop = Math.min(focusRestore.selectionEnd ?? end, end)
      try { target.setSelectionRange(start, stop) } catch {}
    }
  }

  container.querySelector('[data-stepper-prev]')?.addEventListener('click', () => { nav.prev(); renderSteppedShell(root, store, config, nav, variantData) })
  container.querySelector('[data-stepper-next]')?.addEventListener('click', () => { nav.next(); renderSteppedShell(root, store, config, nav, variantData) })
  // When the user manually opens a <details>, sync nav so the next auto-advance
  // lands on the right next step. DO NOT re-render here: newly inserted <details>
  // with open="" fire a toggle on insertion, and re-rendering from inside the
  // handler would immediately recreate those details and refire toggle forever.
  container.querySelectorAll('[data-accordion-step]').forEach(el => {
    el.addEventListener('toggle', () => {
      if (el.open) { nav.goTo(el.dataset.accordionStep); window.__ccShopping?.syncSteps() }
    })
  })

  window.__ccShopping?.syncSteps()
  container.onclick = (e) => {
    const btn = e.target.closest('button')
    if (!btn || btn.disabled) return
    // OOS swatches stay clickable on purpose — selecting one applies the colour
    // to the 3D model so the customer can preview it. ATC is blocked separately
    // via hasUnavailableSelection() so an OOS choice can't actually be ordered.
    const d = btn.dataset
    if (d.editStep)            { nav.goTo(d.editStep); renderSteppedShell(root, store, config, nav, variantData); return }
    if (d.fabricType)          { store.pickFabricType(d.fabricType);                  return }
    if (d.baseThickness)       { store.set('baseThickness', d.baseThickness);         return }
    if (d.armrestType)         { store.set('armrest.type', d.armrestType);            return }
    if (d.extrafunType)        { store.set('extraFun.type', d.extrafunType);          return }
    if (d.regionColour)        { store.setRegionColour(d.regionColour, d.colour);     return }
    if (d.dotcushionEnabled !== undefined) {
      store.set('dotCushion.enabled', d.dotcushionEnabled === 'true')
      return
    }
    for (const idx of [1, 2]) {
      if (d[`cushion${idx}Colour`])     { store.set(`dotCushion.cushion${idx}.fabric`, d[`cushion${idx}Colour`]);           return }
      if (d[`cushion${idx}TextColour`]) { store.set(`dotCushion.cushion${idx}.textColour`, d[`cushion${idx}TextColour`]);   return }
    }
  }

  container.onchange = (e) => {
    const el = e.target
    const d = el.dataset
    if (!d) return
    for (const idx of [1, 2]) {
      if (el.matches(`[data-cushion${idx}-wants-text]`)) {
        const v = el.value
        if (v === '') store.set(`dotCushion.cushion${idx}.wantsText`, null)
        else store.set(`dotCushion.cushion${idx}.wantsText`, v === 'true')
        return
      }
      if (el.matches(`[data-cushion${idx}-font]`)) {
        store.set(`dotCushion.cushion${idx}.font`, el.value || null)
        return
      }
    }
  }

  container.oninput = (e) => {
    const el = e.target
    for (const idx of [1, 2]) {
      if (el.matches(`input[data-cushion${idx}-text-input]`)) {
        store.set(`dotCushion.cushion${idx}.text`, el.value.slice(0, config.text.max))
        return
      }
    }
  }
}

function renderPrice(root, pricing) {
  const el = root.querySelector('[data-price]')
  if (!el) return
  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`
  if (pricing.regular <= 0) {
    // Always render something so the ATC panel doesn't collapse visually.
    el.innerHTML = `<span class="price-current price-current--pending">$0.00</span>`
    return
  }
  if (pricing.onSale) {
    el.innerHTML = `
      <span class="price-compare">${fmt(pricing.compare)}</span>
      <span class="price-current">${fmt(pricing.regular)}</span>
      <span class="price-savings">Save ${fmt(pricing.savings)}</span>
    `
  } else {
    el.innerHTML = `<span class="price-current">${fmt(pricing.regular)}</span>`
  }
}

// Drive the ATC button enabled-state. When the customer has picked an OOS
// variant we keep ATC disabled (the per-swatch "_Colour_ — Out of Stock"
// notice is the customer-facing explanation), and put a short title attribute
// on the button so hover/long-press also surfaces the reason.
function renderATC(root, isComplete, oosCount = 0) {
  const btn = root.querySelector('[data-atc]')
  if (!btn) return
  btn.disabled = !isComplete || oosCount > 0
  if (oosCount > 0) {
    btn.title = oosCount === 1
      ? 'One of your selected colours is out of stock — pick another to continue'
      : `${oosCount} of your selected colours are out of stock — pick others to continue`
  } else {
    btn.removeAttribute('title')
  }
}

function generateBundleId() {
  const rand = Math.random().toString(36).slice(2, 8)
  return `couch-${Date.now()}-${rand}`
}

export function buildCartPayload(state, config, bundleId) {
  return {
    items: getLineItems(state, config).map(line => ({
      id: Number(line.variantId),
      quantity: line.quantity,
      properties: { ...line.properties, _bundleId: bundleId },
    })),
  }
}

// Shopify's native cart listener drops a bulk response when Cart Transform
// merges several requested components into one returned line. Send components
// sequentially, and recover only this attempt if a later request fails.
async function addBundleSequentially(payload) {
  const addedKeys = new Set()
  const bundleIds = new Set(payload.items.map(item => item.properties?._bundleId).filter(Boolean))
  const cartRoot = window.Shopify?.routes?.root || '/'
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' }
  let requestUncertain = false

  function owns(line) {
    const properties = line.properties || {}
    return bundleIds.has(properties._bundleId) || bundleIds.has(properties._ccGiftForBundle)
      || addedKeys.has(line.key) || (line.item_components || []).some(owns)
  }

  async function recover() {
    const response = await fetch(cartRoot + 'cart.js', { headers, cache: 'no-store', signal: AbortSignal.timeout(30000) })
    if (!response.ok) throw new Error('Could not read cart')
    let cart = await response.json()
    let removed = 0
    for (let count = 0; count <= payload.items.length; count++) {
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response')
      const line = cart.items.find(owns)
      if (!line) return removed > 0 || addedKeys.size === 0
      if (!line.key) throw new Error('Could not identify the incomplete bundle')
      const result = await fetch(cartRoot + 'cart/change.js', {
        method: 'POST', headers, body: JSON.stringify({ id: line.key, quantity: 0 }), signal: AbortSignal.timeout(30000)
      })
      if (!result.ok) throw new Error('Could not remove the incomplete bundle')
      cart = await result.json()
      removed++
    }
    throw new Error('Cart recovery did not finish')
  }

  try {
    for (const item of payload.items) {
      requestUncertain = true
      const response = await fetch(cartRoot + 'cart/add.js', {
        method: 'POST', headers, body: JSON.stringify({ items: [item] }), signal: AbortSignal.timeout(30000)
      })
      // Finish reading each response before starting the next addition.
      const body = await response.json()
      requestUncertain = false
      if (!response.ok) {
        const base = body.description || body.message || `Add to cart failed (${response.status})`
        const hint = /sold out|unavailable/i.test(base) ? ' Please pick a different colour or option and try again.' : ''
        throw new Error(base + hint)
      }
      const items = Array.isArray(body.items) ? body.items : [body]
      if (!items.length || items.some(added => !added.key)) {
        requestUncertain = true
        throw new Error('We could not confirm the cart addition.')
      }
      for (const added of items) addedKeys.add(added.key)
    }
  } catch (error) {
    let recovered = false
    try { recovered = await recover() } catch { /* Keep the customer on this page with an explicit review step. */ }
    if (!recovered || requestUncertain) {
      const reviewError = new Error('We could not finish adding your couch. Please check your cart and remove any incomplete couch before trying again.')
      reviewError.requiresCartReview = true
      throw reviewError
    }
    throw error
  }
}

async function addToCart(root, store, config) {
  const btn = root.querySelector('[data-atc]')
  const errEl = root.querySelector('[data-error]')
  if (root.dataset.ccCartAdded === 'true') { window.location.href = '/cart'; return }
  if (!btn || btn.disabled) return
  if (root.dataset.ccCartBusy === 'true' || !store.isComplete()) return
  root.dataset.ccCartBusy = 'true'

  btn.disabled = true
  const originalLabel = btn.textContent
  btn.textContent = 'Adding...'
  if (errEl) { errEl.hidden = true; errEl.textContent = '' }

  try {
    let payload = buildCartPayload(store.getState(), config, generateBundleId())
    if (window.__ccShopping) payload = await window.__ccShopping.prepareCart(payload)
    const sequentialTrial = window.Shopify?.theme?.role === 'unpublished' && new URLSearchParams(window.location.search).get('cc_bundle_tracking_test') === '1'
    if (sequentialTrial) {
      await addBundleSequentially(payload)
    } else {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        // Shopify returns variant-specific "sold out" messages in .description.
        // Append guidance so the customer knows they can change a colour to recover.
        const base = body.description || body.message || `Add to cart failed (${res.status})`
        const hint = /sold out|unavailable/i.test(base)
          ? ' Please pick a different colour or option and try again.'
          : ''
        throw new Error(base + hint)
      }
    }
    root.dataset.ccCartAdded = 'true'
    if (await window.__ccShopping?.verifyCart(payload) !== false) window.location.href = '/cart'
    else { btn.disabled = false; btn.textContent = 'VIEW CART' }
  } catch (err) {
    root.dataset.ccCartBusy = 'false'
    window.__ccShopping?.track('add_to_cart_error')
    console.error('[couch-customiser] ATC failed', err)
    if (errEl) {
      errEl.textContent = err.message || 'Something went wrong. Please try again.'
      errEl.hidden = false
    }
    btn.disabled = false
    btn.textContent = err.requiresCartReview ? 'VIEW CART' : originalLabel
    if (err.requiresCartReview) root.dataset.ccCartAdded = 'true'
  }
}

(function init() {
  if (typeof document === 'undefined') return
  const root = document.querySelector('[data-couch-customiser]')
  if (!root) return

  const configEl = document.querySelector('[data-couch-config]')
  if (!configEl) return
  const config = JSON.parse(configEl.textContent)

  const store = createStore(config)

  const params = new URLSearchParams(window.location.search)
  const encoded = params.get('state')
  if (encoded) {
    const restored = window.FunsquareShopping?.restoreDesign(encoded, config, deserializeState)
    if (restored) store.setState(restored)
  }

  // First-page-load default: if no fabric has been selected (either because
  // there was no URL state or the URL state carried fabricType=null), apply
  // microsuede + sky-blue defaults so the viewer shows a fully-coloured
  // couch with a real price instead of the unpainted GLB placeholder.
  if (store.getState().fabricType == null) {
    store.pickFabricType('microsuede')
  }

  const variantsEl = document.querySelector('[data-couch-variants]')
  let variantData = {}
  if (variantsEl) {
    try { variantData = JSON.parse(variantsEl.textContent) }
    catch (err) { console.warn('[couch-customiser] could not parse variant data', err) }
  }

  const nav = createStepperNav(store)

  function rerender() {
    nav.onStateChange()
    renderSteppedShell(root, store, config, nav, variantData)
    const pricing = computePricing(store.getState(), config, variantData)
    renderPrice(root, pricing)
    const oos = unavailableSelections(store.getState(), config, variantData)
    renderATC(root, store.isComplete(), oos.length)
    if (root.dataset.ccCartBusy === 'true') root.querySelector('[data-atc]').disabled = true
    if (root.dataset.ccCartAdded === 'true') { const b = root.querySelector('[data-atc]'); b.disabled = false; b.textContent = 'VIEW CART' }

    const next = serializeState(store.getState())
    const url = new URL(window.location.href)
    url.searchParams.set('state', next)
    window.history.replaceState({}, '', url)
  }

  store.subscribe(rerender)
  window.__ccStore = store
  window.__ccShopping = window.FunsquareShopping?.createShopping(root, config, store, {serializeState, deserializeState, nav, visibleSteps, renderSteppedShell, variantData, computePricing, getLineItems})
  // RESET_BUTTON_START
  root.querySelector('[data-realism-camera]')?.addEventListener('click', () => {
    if (root.dataset.ccCartBusy === 'true' || root.dataset.ccCartAdded === 'true') return
    nav.goTo('upperBase')
    window.__ccInspiration?.clearSelection()
    store.reset()
  })
  // RESET_BUTTON_END
  window.__ccRealism = window.FunsquareRealism?.createRealism(root, config, store)
  initModelViewer(root, config, store)
  // Element must already be in static markup; initModelViewer() only sets src/poster on it.
  window.__couchViewer = root.querySelector('#couch-viewer')    // Task 15 uses this for dot-cushion text textures

  const atcBtn = root.querySelector('[data-atc]')
  if (atcBtn) atcBtn.addEventListener('click', () => addToCart(root, store, config))

  rerender()
  window.__ccInspiration = window.FunsquareInspiration?.mount(root, config, store, {nav, rerender})
})()

// ============ 3D integration (single-material GLB, node-level control) ============
//
// The Funsquare couch GLB ships with ONE shared material ("Sofa") across all
// meshes, but meshes have semantic node names. We:
//   1. Clone the shared material per mesh on load so each can carry its own
//      colour independent of the others.
//   2. Group nodes by role (base halves per thickness, trapezium-set pieces
//      per thickness, armrest shapes, dot cushions) and drive both visibility
//      and baseColor from state.
// See docs/superpowers/plans/2026-04-20-funsquare-couch-customiser-replacement.md
// Task 14 for the scope decision (Path A: code around single material).

// `visibleIf` returns true on null state only for thin bases + Trapezium so the
// default-load render matches LIVE's default Angle3D scene (thin+thick bases +
// trapezium backrest, no armrests, no cushions, no extra-fun). Once the user
// picks, the selection drives visibility strictly.
const NODE_GROUPS = {
  baseThinUpper:    { nodes: ['baseThinTop'],    colourFrom: s => s.upperBase, visibleIf: s => s.baseThickness == null || s.baseThickness === 'thin-and-thick' },
  baseThinLower:    { nodes: ['baseThinBottom'], colourFrom: s => s.lowerBase, visibleIf: s => s.baseThickness == null || s.baseThickness === 'thin-and-thick' },
  baseThickUpper:   { nodes: ['baseThickTop'],   colourFrom: s => s.upperBase, visibleIf: s => s.baseThickness === 'double-thick'  },
  baseThickLower:   { nodes: ['baseThickBottom'],colourFrom: s => s.lowerBase, visibleIf: s => s.baseThickness === 'double-thick'  },
  trapeziumCore:    { nodes: ['Trapezium'],      colourFrom: s => s.trapezium, visibleIf: () => true },
  armrestCuboids:   { nodes: ['Armrest_cuboid_1'],         colourFrom: s => s.armrest.colour, visibleIf: s => s.armrest.type === 'cuboids' },
  armrestCylinders: { nodes: ['Armrest_cylinder_1'],       colourFrom: s => s.armrest.colour, visibleIf: s => s.armrest.type === 'cylinders' },
  armrestHalfTraps: { nodes: ['Armrest_half_trapezium_1'], colourFrom: s => s.armrest.colour, visibleIf: s => s.armrest.type === 'half-trapeziums' },
  cushion1:         { nodes: ['CircleCushion1'], colourFrom: s => s.dotCushion.cushion1.fabric, visibleIf: s => s.dotCushion.enabled === true },
  cushion2:         { nodes: ['CircleCushion2'], colourFrom: s => s.dotCushion.cushion2.fabric, visibleIf: s => s.dotCushion.enabled === true },
}

// Maps state.extraFun.type (config key) -> GLB mesh node names (both thickness variants).
// Andrew's product lineup: cuboid, cylinder, half-trapezium, folding-circle (aka "Folding Slope").
// The selected shape's meshes are made visible for the CURRENT baseThickness only.
export const EXTRA_FUN_NODE_MAP = {
  'cuboids':         ['extraThin_cuboid_1',         'extraThick_cuboid_1'],
  'cylinders':       ['extraThin_cylinder_1',       'extraThick_cylinder_1'],
  'half-trapeziums': ['extraThin_half_trapezium_1', 'extraThick_half_trapezium_1'],
  'folding-circle':  ['extraThin_folding_circle_1', 'extraThick_folding_circle_1'],
}

// All folding-circle mesh variants (both rendered and hidden based on base thickness).
// This list ensures both the selected variant AND the unused variant are hidden during hide-all pass,
// avoiding phantom meshes if a swap happens without explicitly hiding the previous variant.
export const ALL_FOLDING_CIRCLE_NODES = Object.freeze([
  'extraThin_folding_circle_1',   // flat disc (visible when Thin base selected)
  'extraThin_folding_circle_2',   // half-moon (hidden when Thin base selected)
  'extraThick_folding_circle_1',  // half-moon (visible when Thick base selected)
  'extraThick_folding_circle_2',  // flat disc (hidden when Thick base selected)
])

// Flat list of every possible Extra Fun mesh name — used to hide-all before show-one.
export const ALL_EXTRA_FUN_NODES = Object.freeze([
  ...EXTRA_FUN_NODE_MAP.cuboids,
  ...EXTRA_FUN_NODE_MAP.cylinders,
  ...EXTRA_FUN_NODE_MAP['half-trapeziums'],
  ...ALL_FOLDING_CIRCLE_NODES,
])

function getModelScene(viewer) {
  // funsquare-model-viewer 1.0.0 (renamed fork of tda-model-viewer 1.7.2 — itself a
  // Google model-viewer v1.x fork) and Google v3 both hide the THREE.Scene behind
  // internal storage. Probe known accessors in order.
  if (!viewer) return null
  const sym = Object.getOwnPropertySymbols(viewer).find(s => s.description === 'scene')
  if (sym && viewer[sym]) return viewer[sym]
  if (viewer.scene) return viewer.scene
  if (viewer.model && viewer.model.scene) return viewer.model.scene
  if (viewer[Symbol.for('scene')]) return viewer[Symbol.for('scene')]
  return null
}

function setNodeColour(node, hexString) {
  const m = node && node.material
  if (!m) return
  if (m.color && typeof m.color.set === 'function') {
    m.color.set(hexString)
  } else if (typeof m.setValues === 'function') {
    // setValues internally calls this.color.set(); only safe if .color exists.
    try { m.setValues({ color: hexString }) } catch { /* material type lacks color */ }
  }
  m.needsUpdate = true
}

// ============ Per-colour baseColor textures (LIVE parity) ============
//
// LIVE's Angle3D binds a small tileable PNG per fabric+colour to
// material.map instead of tinting a solid material.color. This gives
// the fabric weave pattern baked into the pixel data and produces the
// clean, uniform look LIVE shows. We self-host 23 textures (17
// microsuede + 6 corduroy) under config.fabricColourTextures.
//
// Textures are loaded lazily on first use per (fabric, colour) pair and
// cached for the session.

const _colourTextureCache = new Map()  // key: `${fabric}|${colour}` → Promise<Texture>

function loadColourTextureImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`colour texture load failed: ${url}`))
    img.src = url
  })
}

function getColourTexture(fabric, colour, config, templateTexture) {
  if (!fabric || !colour) return Promise.resolve(null)
  const key = `${fabric}|${colour}`
  if (_colourTextureCache.has(key)) return _colourTextureCache.get(key)
  const table = config && config.fabricColourTextures && config.fabricColourTextures[fabric]
  const url = table && table[colour]
  if (!url) return Promise.resolve(null)
  if (!templateTexture || typeof templateTexture.clone !== 'function') return Promise.resolve(null)
  // funsquare-model-viewer 1.0.0 (tda 1.7.2 fork) ships three.js r128 — Texture has .image only, no .source wrapper.
  // Google model-viewer v3 ships r151+ — Texture has .source.{constructor, data}.
  // Detect which API is active and write through the matching path.
  const useSourceAPI = !!(templateTexture.source && templateTexture.source.constructor)
  const SourceCtor = useSourceAPI ? templateTexture.source.constructor : null
  const p = loadColourTextureImage(url)
    .then(img => {
      const t = templateTexture.clone()
      if (useSourceAPI) {
        t.source = new SourceCtor(img)
      } else {
        t.image = img
      }
      // baseColor textures need sRGB colour space so the PBR shader reads
      // them linearly — Three.js defaults newly-cloned textures to sRGB on
      // colour maps, but be explicit. r128 uses .encoding=3001 (sRGBEncoding);
      // r152+ uses .colorSpace='srgb'. Keep template's encoding by default.
      if ('colorSpace' in t) t.colorSpace = 'srgb'
      // Tile the texture — LIVE's baseColors are small repeating patches.
      // The GLB's UV unwrap already covers the full mesh, but the default
      // wrap mode is clamp which would stretch a tiny texture across the
      // whole face. Use repeat wrap so the weave pattern tiles naturally.
      if ('wrapS' in t) t.wrapS = 1000  // THREE.RepeatWrapping
      if ('wrapT' in t) t.wrapT = 1000
      t.needsUpdate = true
      return t
    })
    .catch(err => {
      console.warn('[couch-customiser] colour texture failed:', key, err)
      _colourTextureCache.delete(key)
      return null
    })
  _colourTextureCache.set(key, p)
  return p
}

// Apply a fabric+colour pair to a node. When a per-colour baseColor
// texture is configured, swap material.map and neutralise material.color
// to white so the texture shows unmodulated. Falls back to the classic
// hex-tint path if no texture URL is configured.
// `onReady` is invoked after the async texture load finishes so the
// caller can trigger scene.queueRender() — model-viewer won't otherwise
// re-render after the synchronous applyStateTo3D pass has returned.
function setNodeColourAndTexture(node, fabric, colour, hex, config, onReady) {
  const m = node && node.material
  if (!m) return
  let template = m.map
  const selection = `${fabric}|${colour}`
  node.userData.ccSelection = selection
  // Try the texture path first.
  if (fabric && colour && template) {
    getColourTexture(fabric, colour, config, template).then(tex => {
      if (!tex || !node.material || node.userData.ccSelection !== selection) return
      node.material.map = tex
      if (node.material.color && typeof node.material.color.set === 'function') {
        node.material.color.setRGB(...(config.fabricPreviewTints?.[fabric]?.[colour] || [1, 1, 1]))  // Subtle per-colour calibration; retain the original fabric texture.
      }
      window.__ccRealism?.decorateNode(node, fabric, colour, hex)
      node.material.needsUpdate = true
      if (typeof onReady === 'function') onReady()
    })
    return
  }
  // Fallback: hex tint (no texture available).
  setNodeColour(node, hex)
  window.__ccRealism?.decorateNode(node, fabric, colour, hex)
}

async function initModelViewer(root, config, store) {
  if (typeof customElements !== 'undefined' && customElements.whenDefined) {
    try { await customElements.whenDefined('funsquare-model-viewer') } catch { /* unsupported element name */ }
  }
  const viewer = root.querySelector('#couch-viewer')
  if (!viewer) return null

  // ============ Loading overlay ============
  // Show "Setting up your customiser…" only if the GLB takes longer than 300 ms
  // to load. Cached/fast loads skip the overlay entirely so it never flashes.
  const loadingEl = root.querySelector('[data-viewer-loading]')
  const progressFill = root.querySelector('[data-viewer-progress-fill]')
  let showLoaderTimer = null
  if (loadingEl) {
    showLoaderTimer = setTimeout(() => {
      loadingEl.classList.add('is-visible')
      loadingEl.removeAttribute('aria-hidden')
    }, 300)
  }
  const hideLoader = () => {
    if (showLoaderTimer) { clearTimeout(showLoaderTimer); showLoaderTimer = null }
    if (!loadingEl) return
    loadingEl.classList.remove('is-visible')
    loadingEl.setAttribute('aria-hidden', 'true')
  }
  // Drive the progress bar from model-viewer's `progress` event. Spec shape:
  // event.detail = { url, totalProgress: 0..1 }. The fork uses the same payload.
  if (progressFill) {
    viewer.addEventListener('progress', (ev) => {
      const p = ev?.detail?.totalProgress
      if (typeof p === 'number') progressFill.style.width = `${Math.round(p * 100)}%`
    })
  }
  // `load` is the canonical signal that the GLB is parsed and renderable.
  viewer.addEventListener('load', hideLoader, { once: true })
  // If the GLB fails (network error, CORS, malformed), keep customers from
  // staring at a forever-spinner — just hide the overlay so the poster image
  // remains visible. ATC is unaffected; nothing else depends on the GLB.
  viewer.addEventListener('error', hideLoader, { once: true })

  // model-viewer v3's `src`/`poster` are @property without reflect — setAttribute is required
  // to update the DOM attribute AND trigger the internal load pipeline.
  viewer.setAttribute('src', window.__ccRealism?.modelUrl() || config.model.glbUrl)
  if (config.model.posterUrl) viewer.setAttribute('poster', config.model.posterUrl)

  const fabricHex = Object.fromEntries(config.fabricColours.map(c => [c.key, c.hex]))
  const nodesByName = new Map()
  let scene = null
  let loaded = false

  // model-viewer batches renders; material/visibility mutations done outside its own
  // animation loop don't re-render unless we explicitly ask for a frame.
  // - Google model-viewer v3 exposes scene.queueRender()
  // - funsquare-model-viewer 1.0.0 (r128 tda fork) exposes scene.isDirty=true (re-renders on next frame)
  //   and also has scene.dispatchEvent({type: 'render'}) — try both paths.
  const kickRender = () => {
    if (!scene) return
    window.__ccRealism?.updateGround()
    if (scene.shadow) scene.shadow.needsUpdate = true
    if (typeof scene.queueRender === 'function') { scene.queueRender(); return }
    scene.isDirty = true  // r128/funsquare-fork — flagged in their render loop
  }

  viewer.addEventListener('load', () => {
    scene = getModelScene(viewer)
    if (!scene || typeof scene.traverse !== 'function') {
      console.warn('[couch-customiser] No scene access — colour/visibility swap disabled.')
      return
    }
    nodesByName.clear()
    scene.traverse((obj) => {
      if (obj.name) nodesByName.set(obj.name, obj)
      if (obj.isMesh && obj.material && typeof obj.material.clone === 'function') {
        obj.material = obj.material.clone()
        // LIVE renders with MeshStandardMaterial; model-viewer's loader promotes to
        // MeshPhysicalMaterial (clearcoat/sheen/iridescence slots) which makes surfaces
        // read glossier/darker even at default 0. Downgrade to match LIVE's shading.
        // Check by instance.type — the bundled Three.js is minified so constructor.name is unreliable.
        const mat = obj.material
        if (mat && mat.type === 'MeshPhysicalMaterial') {
          const StandardCtor = Object.getPrototypeOf(mat.constructor)
          try {
            const std = new StandardCtor()
            if (std && std.type === 'MeshStandardMaterial' && typeof std.copy === 'function') {
              std.copy(mat)
              obj.material = std
            }
          } catch (_) { /* leave as-is if downgrade fails */ }
        }
      }
    })
    loaded = true
    window.__couchNodes = nodesByName
    window.__ccRealism?.onModelLoad(nodesByName, () => {
      applyStateTo3D(nodesByName, fabricHex, store.getState(), config, kickRender)
      applyCorduroyMaps(nodesByName, config, store.getState(), kickRender)
      scheduleCushionTexts(scene, nodesByName, config, store.getState(), kickRender)
      kickRender()
    })
    applyStateTo3D(nodesByName, fabricHex, store.getState(), config, kickRender)
    applyCorduroyMaps(nodesByName, config, store.getState(), kickRender)
    scheduleCushionTexts(scene, nodesByName, config, store.getState(), kickRender)
    kickRender()
  })

  store.subscribe((state) => {
    if (!loaded) return
    applyStateTo3D(nodesByName, fabricHex, state, config, kickRender)
    applyCorduroyMaps(nodesByName, config, state, kickRender)
    scheduleCushionTexts(scene, nodesByName, config, state, kickRender)
    kickRender()
  })

  return viewer
}

function applyStateTo3D(nodesByName, fabricHex, state, config, onTextureReady) {
  if (!nodesByName.size) return
  const fabric = state && state.fabricType

  // Step 1: hide ALL Extra Fun meshes first, then selectively show the chosen shape.
  for (const name of ALL_EXTRA_FUN_NODES) {
    const n = nodesByName.get(name)
    if (n) n.visible = false
  }
  const extraType = state.extraFun && state.extraFun.type
  if (extraType && extraType !== 'none' && EXTRA_FUN_NODE_MAP[extraType]) {
    const chosen = EXTRA_FUN_NODE_MAP[extraType]
    const thinMode = state.baseThickness === 'thin-and-thick'
    const thicknessNames = chosen.filter(n =>
      thinMode ? n.startsWith('extraThin_') : n.startsWith('extraThick_')
    )
    const colourKey = state.extraFun.colour
    const hex = colourKey ? fabricHex[colourKey] : null
    for (const name of thicknessNames) {
      const n = nodesByName.get(name)
      if (!n) continue
      n.visible = true
      if (colourKey) setNodeColourAndTexture(n, fabric, colourKey, hex, config, onTextureReady)
      else if (!n.material) console.warn('[3D] mesh has no material:', name)
    }
  }

  // Step 2: per-region groups.
  for (const group of Object.values(NODE_GROUPS)) {
    const visible = group.visibleIf(state)
    const colourKey = group.colourFrom(state)
    const hex = colourKey ? fabricHex[colourKey] : null
    for (const name of group.nodes) {
      const node = nodesByName.get(name)
      if (!node) continue
      node.visible = visible
      if (colourKey) setNodeColourAndTexture(node, fabric, colourKey, hex, config, onTextureReady)
    }
  }
}

// ============ Fabric material maps (microsuede + corduroy) ============
//
// Both fabrics get a normalMap (different rib/weave pattern per fabric) and
// share a single ORM texture for occlusion/roughness/metallic. All three
// textures are copied from LIVE's Angle3D configurator and self-hosted in
// assets/. Textures load lazily on first use and are cached for the session.

function loadFabricImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`load failed: ${url}`))
    img.src = url
  })
}

const _fabricTextureCache = { microsuede: null, corduroy: null, orm: null, _promises: {} }

function getFabricTextureSet(fabricKind, config, templateTexture) {
  if (_fabricTextureCache._promises[fabricKind]) return _fabricTextureCache._promises[fabricKind]
  const urls = config && config.fabricTextures
  if (!urls) return Promise.resolve(null)
  const normalUrl = fabricKind === 'corduroy' ? urls.corduroyNormalUrl : urls.microsuedeNormalUrl
  const ormUrl = urls.sharedOrmUrl
  if (!normalUrl || !ormUrl) return Promise.resolve(null)
  if (!templateTexture || typeof templateTexture.clone !== 'function') return Promise.resolve(null)
  // r128 (funsquare-/tda-fork) uses Texture.image; r151+ (Google v3) uses Texture.source.{constructor,data}.
  const useSourceAPI = !!(templateTexture.source && templateTexture.source.constructor)
  const SourceCtor = useSourceAPI ? templateTexture.source.constructor : null

  const makeTex = (img) => {
    const t = templateTexture.clone()
    if (useSourceAPI) {
      t.source = new SourceCtor(img)
    } else {
      t.image = img
    }
    t.needsUpdate = true
    return t
  }

  _fabricTextureCache._promises[fabricKind] = Promise.all([
    loadFabricImage(normalUrl),
    _fabricTextureCache.orm ? Promise.resolve(_fabricTextureCache.orm) : loadFabricImage(ormUrl).then(img => makeTex(img)),
  ])
    .then(([normalImg, ormTex]) => {
      _fabricTextureCache.orm = ormTex
      const normal = makeTex(normalImg)
      const pack = { normal, orm: ormTex }
      _fabricTextureCache[fabricKind] = pack
      return pack
    })
    .catch((err) => {
      console.warn('[couch-customiser] fabric texture load failed:', fabricKind, err)
      _fabricTextureCache._promises[fabricKind] = null
      return null
    })
  return _fabricTextureCache._promises[fabricKind]
}

// Per-fabric rendering parameters. Matches LIVE's Angle3D configuration:
// normalScale (1, -1) — full strength with Y flipped (GLTF vs Three.js
// convention difference that the original author compensated for). Pair
// with roughnessMap + metalnessMap BOTH bound to the shared ORM and the
// per-texel roughness smooths out what would otherwise be shiny spots
// from the normalMap alone. metalness = 1 is safe because the ORM's
// blue channel is ~0 everywhere, giving a net non-metallic fabric.
const FABRIC_MATERIAL_PROFILE = {
  microsuede: { normalScale: 1, normalScaleY: -1, envMapIntensity: 1 },
  corduroy:   { normalScale: 1, normalScaleY: -1, envMapIntensity: 1 },
}

function applyFabricMaps(nodesByName, config, state, onReady) {
  if (!nodesByName || !nodesByName.size) return
  const fabric = state && state.fabricType
  if (fabric !== 'microsuede' && fabric !== 'corduroy') {
    // No fabric picked yet — strip any overlay maps so the GLB's default shows.
    let changed = false
    for (const node of nodesByName.values()) {
      const m = node && node.material
      if (!m) continue
      if (m.normalMap || m.roughnessMap || m.metalnessMap || m.aoMap) {
        m.normalMap = null
        m.roughnessMap = null
        m.metalnessMap = null
        m.aoMap = null
        if (m.normalScale && typeof m.normalScale.set === 'function') m.normalScale.set(1, 1)
        m.envMapIntensity = 1
        m.needsUpdate = true
        changed = true
      }
    }
    if (changed && typeof onReady === 'function') onReady()
    return
  }
  let template = null
  for (const node of nodesByName.values()) {
    if (node && node.material && node.material.map) { template = node.material.map; break }
  }
  const profile = FABRIC_MATERIAL_PROFILE[fabric]
  getFabricTextureSet(fabric, config, template).then((tex) => {
    if (!tex || window.__ccStore?.getState().fabricType !== fabric) return
    for (const node of nodesByName.values()) {
      const m = node && node.material
      if (!m) continue
      m.normalMap = tex.normal
      if (m.normalScale && typeof m.normalScale.set === 'function') {
        m.normalScale.set(profile.normalScale, profile.normalScaleY)
      }
      // LIVE parity: bind the ORM to both roughnessMap and metalnessMap,
      // keep scalar roughness/metalness = 1 as pass-through multipliers.
      // The ORM's green channel drives per-texel roughness (smoothing
      // specular highlights across the fabric), blue channel drives
      // metallic (≈0 everywhere so fabric stays non-metallic).
      m.roughnessMap = tex.orm
      m.metalnessMap = tex.orm
      m.roughness = 1
      m.metalness = 1
      m.aoMap = null
      m.envMapIntensity = profile.envMapIntensity
      m.needsUpdate = true
    }
    window.__ccRealism?.decorateAll()
    if (typeof onReady === 'function') onReady()
  })
}

// Back-compat shim — callers still reference applyCorduroyMaps via name.
const applyCorduroyMaps = applyFabricMaps

// ============ Dot-cushion text (overlay mesh, LIVE-parity) ============
//
// LIVE's Angle3D widget does NOT modify the cushion's own material. It adds
// a separate overlay Mesh to the TOP-LEVEL ModelScene per cushion with text.
// The overlay has its own simple plane geometry with clean [0,1]x[0,1] UVs
// and a MeshPhongMaterial whose `map` is a 512x512 canvas. Transparent
// background + opaque text pixels render as floating embroidered text on
// top of the cushion.
//
// Why overlay-on-top and not paint-cushion-map: the cushion's GLB geometry
// has a weave-tile-friendly UV unwrap — the mesh samples an unpredictable
// sub-region of the texture atlas. Painting text at the UV bbox centre
// doesn't land on the visible top face. LIVE sidesteps this by giving the
// overlay its OWN clean UVs.
//
// THREE is pulled from unpkg via dynamic import so we can construct real
// MeshPhongMaterial (model-viewer's bundled THREE isn't exposed). unpkg is
// already trusted — model-viewer itself loads from there.

const TEXT_CANVAS_SIZE = 512
const THREE_CDN_URL = 'https://unpkg.com/three@0.161.0/build/three.module.js'

const _fontReadyCache = new Map()
function ensureFontReady(fontLabel) {
  if (_fontReadyCache.has(fontLabel)) return _fontReadyCache.get(fontLabel)
  const p = (document.fonts && typeof document.fonts.load === 'function')
    ? document.fonts.load(`48px "${fontLabel}"`).then(() => undefined).catch(() => undefined)
    : Promise.resolve()
  _fontReadyCache.set(fontLabel, p)
  return p
}

function loadCouchTHREE() {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (window.__couchTHREE) return Promise.resolve(window.__couchTHREE)
  if (!window.__couchTHREEPromise) {
    window.__couchTHREEPromise = import(/* @vite-ignore */ THREE_CDN_URL)
      .then(m => { window.__couchTHREE = m; return m })
      .catch(err => {
        console.warn('[couch-customiser] THREE import failed:', err)
        window.__couchTHREEPromise = null
        return null
      })
  }
  return window.__couchTHREEPromise
}

function hexToNumber(hex) {
  return parseInt(String(hex).replace('#', ''), 16)
}

function paintTextCanvas(canvas, text, fontLabel, textHex) {
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // The overlay UVs map the cushion's full XY bbox to [0..1]. A disc's
  // visible circular face occupies the inscribed circle, so keep text
  // inside ~70% of the canvas width. Y is drawn slightly above canvas
  // midline so that, after the UV flip, the text sits at the visual
  // centre of the cushion face.
  const maxWidth = canvas.width * 0.7
  let size = Math.floor(canvas.height * 0.32)
  ctx.font = `${size}px "${fontLabel}", sans-serif`
  while (ctx.measureText(text).width > maxWidth && size > 18) {
    size -= 4
    ctx.font = `${size}px "${fontLabel}", sans-serif`
  }
  ctx.fillStyle = textHex
  ctx.fillText(text, canvas.width / 2, canvas.height * 0.44)
}

// Build overlay mesh once per cushion. LIVE-parity: clone the cushion's
// own geometry, bake its world transform into vertex positions, and keep
// the overlay at identity transform in the top-level scene. Replace UVs
// with a planar world-XY projection so the 512x512 text canvas lands
// centred on the cushion's front face. Because the overlay shares the
// cushion's 3D surface curvature, the text follows the cushion at every
// camera angle (not just front-on).
function ensureOverlayMesh(scene, cushion, THREE) {
  if (cushion.userData && cushion.userData._textOverlay) {
    return cushion.userData._textOverlay
  }

  const canvas = document.createElement('canvas')
  canvas.width = TEXT_CANVAS_SIZE
  canvas.height = TEXT_CANVAS_SIZE

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true

  const material = new THREE.MeshPhongMaterial({
    map: texture,
    transparent: true,
    alphaTest: 0,
    side: THREE.FrontSide,
    depthTest: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: 0,
    color: 0xffffff,
  })

  cushion.updateMatrixWorld(true)
  const geometry = cushion.geometry.clone()
  geometry.applyMatrix4(cushion.matrixWorld)
  geometry.computeBoundingBox()

  // Planar UVs mapping world X,Y -> [0..1]. Canvas center maps to face
  // center. FrontSide ensures text only renders on the +Z-facing front.
  const bb = geometry.boundingBox
  const sizeX = bb.max.x - bb.min.x || 1
  const sizeY = bb.max.y - bb.min.y || 1
  const pos = geometry.attributes.position
  const uv = new Float32Array(pos.count * 2)
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2]     = (pos.getX(i) - bb.min.x) / sizeX
    uv[i * 2 + 1] = (pos.getY(i) - bb.min.y) / sizeY
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))

  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = `${cushion.name}_TextOverlay`
  mesh.renderOrder = 0
  mesh.visible = false

  scene.add(mesh)

  if (!cushion.userData) cushion.userData = {}
  cushion.userData._textOverlay = { mesh, material, texture, canvas }
  return cushion.userData._textOverlay
}

async function applyCushionTexts(scene, nodesByName, config, state) {
  if (!scene || !nodesByName || !nodesByName.size) return
  const THREE = await loadCouchTHREE()
  if (!THREE) return

  for (const idx of [1, 2]) {
    const cushion = nodesByName.get(`CircleCushion${idx}`)
    if (!cushion) continue
    const slot = state.dotCushion[`cushion${idx}`]
    const wantsText = state.dotCushion.enabled === true &&
                      slot.wantsText === true &&
                      state.fabricType === 'microsuede' &&
                      !!slot.text

    const existing = cushion.userData && cushion.userData._textOverlay

    if (!wantsText) {
      if (existing) existing.mesh.visible = false
      continue
    }

    const fontLabel = config.dotFonts.find(f => f.key === slot.font)?.label
    const textHex = config.dotTextColours.find(c => c.key === slot.textColour)?.hex
    if (!fontLabel || !textHex) continue
    await ensureFontReady(fontLabel)
    if (window.__ccStore?.getState() !== state || nodesByName.get(`CircleCushion${idx}`) !== cushion) return

    const ref = ensureOverlayMesh(scene, cushion, THREE)
    paintTextCanvas(ref.canvas, slot.text, fontLabel, textHex)
    ref.texture.needsUpdate = true
    if (ref.material.emissive && typeof ref.material.emissive.setHex === 'function') {
      ref.material.emissive.setHex(hexToNumber(textHex))
    }
    ref.material.needsUpdate = true
    ref.mesh.visible = cushion.visible !== false
  }
}

let _textApplyTimer = null
function scheduleCushionTexts(scene, nodesByName, config, state, onApplied) {
  if (typeof document === 'undefined') return
  if (_textApplyTimer) clearTimeout(_textApplyTimer)
  _textApplyTimer = setTimeout(() => {
    const p = applyCushionTexts(scene, nodesByName, config, state)
    if (p && typeof p.then === 'function' && typeof onApplied === 'function') {
      p.then(onApplied, onApplied)
    } else if (typeof onApplied === 'function') {
      onApplied()
    }
  }, 200)
}
